import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { getCategory } from '../data/categories';
import { getProductsByCategory } from '../data/products';
import { Button } from '../components/ui/button';
import OptimizedImage from '../components/ui/OptimizedImage';
import SEO from '../components/SEO/SEO';
import { breadcrumbSchema, faqSchema } from '../components/SEO/structuredData';

function CategoryPage() {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    const fetchCategoryAndProducts = () => {
      // Get category information
      const categoryData = getCategory(categoryId);
      
      if (categoryData) {
        setCategory(categoryData);
        
        // Get products for this category
        const categoryProducts = getProductsByCategory(categoryId);
        setProducts(categoryProducts);
      }
      
      setLoading(false);
    };
    
    fetchCategoryAndProducts();
  }, [categoryId]);

  // SEO data
  const breadcrumbs = category ? breadcrumbSchema([
    { name: 'Accueil', url: '/' },
    { name: 'Catégories', url: '/#categories' },
    { name: category.title, url: `/category/${categoryId}` }
  ]) : null;

  const structuredData = category && category.faq ? {
    "@context": "https://schema.org",
    "@graph": [breadcrumbs, faqSchema(category.faq)]
  } : breadcrumbs;

  if (loading) {
    return (
      <div className="pt-24 pb-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Chargement de la catégorie...</p>
      </div>
    );
  }

  if (!category) {
    return (
      <>
        <SEO 
          title="Catégorie non trouvée | San Metal by Ben Amor"
          description="La catégorie demandée n'existe pas. Découvrez notre gamme complète de produits métallurgiques."
          canonical={`/category/${categoryId}`}
        />
        <div className="pt-24 pb-20 text-center">
        <h2 className="text-2xl font-bold text-gray-800">Catégorie non trouvée</h2>
        <p className="mt-4 text-gray-600">
          Nous n'avons pas trouvé la catégorie que vous recherchez.
        </p>
        <Link to="/" className="mt-6 inline-block">
          <Button variant="primary">
            Retour à l'accueil
          </Button>
        </Link>
      </div>
    </>);
  }

  return (
    <>
      <SEO 
        title={`${category.title} | San Metal by Ben Amor`}
        description={category.description || `Découvrez notre gamme complète de ${category.title.toLowerCase()} chez San Metal by Ben Amor. Produits métallurgiques de qualité supérieure pour tous vos projets de construction et industrie en Tunisie.`}
        keywords={`${category.title}, ${category.title} Tunisie, achat ${category.title.toLowerCase()}, fournisseur ${category.title.toLowerCase()}, ${category.title} Sfax`}
        canonical={`/category/${categoryId}`}
        structuredData={structuredData}
      />
      <div className="mt-[120px] sm:mt-[130px] md:mt-[140px] lg:mt-[140px] bg-gray-100 min-h-screen">
        <div className="container mx-auto max-w-7xl px-4">
          {/* Première boîte pour la description et le titre */}
          <div className="bg-white shadow-lg p-8 md:p-12 mb-8 mt-8">
            {/* Navigation */}
            <nav className="text-sm text-gray-500 mb-2">
              <Link to="/" className="hover:scale-105 rounded-none">Accueil</Link>
              <span className="mx-1">&gt;</span>
              <Link to="/#products" className="hover:scale-105 rounded-none">Produits</Link>
              <span className="mx-1">&gt;</span>
              <span>{category.title}</span>
            </nav>

          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">{category.title}</h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
            {category.description}
          </p>
        </div>

        {/* Deuxième boîte pour la liste des produits */}
        <div className="bg-white shadow-lg p-8 md:p-12">
          {/* Titre de la section des produits */}
          <h2 className="text-3xl font-bold text-gray-800 text-left mb-8">{category.productsTitle || (category.title === 'Ossature métallique' || category.title === 'Aciers Spéciaux' ? category.title : `${category.title} disponibles`)}</h2>

          {/* Grille des produits */}
          {products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {products.map((product) => (
                <Link to={`/products/${product.id}`} key={product.id}>
                  <div className="bg-white shadow-lg overflow-hidden transform transition-transform duration-300 hover:scale-105 rounded-none">
                    <OptimizedImage 
                      src={product.image || `https://placehold.co/600x400/F2F2F2/000000?text=${product.title}`} 
                      alt={`Image d'une ${product.title}`} 
                      className="w-full h-48 object-cover"
                      height="192px"
                    />
                    <div className="p-6 text-left">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{product.title}</h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">Aucun produit disponible dans cette catégorie pour le moment.</p>
            </div>
          )}
        </div>

        {/* Sections de contenu (optionnelles) */}
        {category.sections && (
          <div className="bg-white shadow-lg p-8 md:p-12 mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {category.sections.map((section) => (
                <div key={section.title}>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{section.title}</h2>
                  <div className="w-12 h-1 bg-red-600 mb-4"></div>
                  <p className="text-gray-600 mb-4 leading-relaxed">{section.text}</p>
                  <ul className="space-y-2">
                    {section.items.map((item) => (
                      <li key={item} className="flex items-start space-x-3">
                        <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Points forts (optionnels) */}
        {category.highlights && (
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 shadow-lg p-8 md:p-12 mt-8">
            <h2 className="text-3xl font-bold text-white text-left mb-8">Pourquoi choisir SAN METAL BY BEN AMOR ?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {category.highlights.map((highlight) => (
                <div key={highlight.title} className="bg-white/10 border border-white/20 p-6">
                  <div className="w-10 h-1 bg-red-600 mb-4"></div>
                  <h3 className="text-lg font-semibold text-white mb-2">{highlight.title}</h3>
                  <p className="text-sm text-gray-300">{highlight.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAQ (optionnelle) */}
        {category.faq && (
          <div className="bg-white shadow-lg p-8 md:p-12 mt-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-800 text-left mb-8">Questions fréquentes</h2>
            <div className="divide-y divide-gray-200 border-t border-b border-gray-200">
              {category.faq.map((item) => (
                <details key={item.question} className="group py-5">
                  <summary className="flex justify-between items-center cursor-pointer list-none [&::-webkit-details-marker]:hidden text-lg font-semibold text-gray-900 hover:text-red-700">
                    {item.question}
                    <ChevronRight className="h-5 w-5 text-red-600 flex-shrink-0 ml-4 transition-transform duration-200 group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-gray-600 leading-relaxed">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
}

export default CategoryPage;
